import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { KeepProportions, ProportionSetting } from '@gik/ui';

type ImageBaseProps = React.HTMLAttributes<HTMLElement> & {
  /**
   * url to image
   * This is optional because the url to the image could be fetched from the backend.
   * TODO: In that case this component should display itself in a loading state.
   */
  src?: string | string[];
  srcSet?: string;
  alt?: string;
  inline?: boolean;
};

type ImageAsImgProps = ImageBaseProps & {
  fullWidth?: boolean;
};

type ImageAsBackgroundProps = ImageBaseProps & {
  asBackground: boolean;
  proportion: ProportionSetting;
};

export type ImageProps = ImageAsImgProps | ImageAsBackgroundProps;

const blockName = 'image';

/**
 * Image component.
 *
 * This component will take care of displaying a fallback if no image was provided.
 *
 * TODO: this could also show a loading spinner or skeleton while the image is loading.
 */
function Image(props: ImageProps): React.ReactElement {
  const bem = bemBlock(blockName);

  if (!props.src) {
    return (
      <div className={bem(null, 'fallback', [props.className])}>
        image
        <br />
        coming
        <br />
        soon
      </div>
    );
  }

  // TODO: refactor move this code to BackgroundImage
  if ((props as ImageAsBackgroundProps).asBackground) {
    const { src, className, style, proportion, ...otherProps } = props as ImageAsBackgroundProps;
    delete otherProps['inline'];

    let finalSrc: string;

    if (Array.isArray(src)) {
      finalSrc = src[0];
    } else {
      finalSrc = src;
    }

    return (
      <KeepProportions
        proportion={proportion}
        className={bem(
          null,
          [
            {
              'as-background': true,
            },
          ],
          [className]
        )}
      >
        <div
          style={
            finalSrc
              ? {
                  ...style,
                  backgroundImage: `url('${finalSrc}')`,
                }
              : style
          }
          {...otherProps}
        />
      </KeepProportions>
    );
  } else {
    const { src, className, style, fullWidth, alt, srcSet, inline, ...otherProps } = props as ImageAsImgProps;
    delete otherProps['asBackground'];

    let finalSrc: string;
    let finalSrcSet: string = srcSet;

    if (Array.isArray(src)) {
      finalSrc = src[0];
      finalSrcSet = src.join(', ');
    } else {
      finalSrc = src;
    }

    return (
      <img
        className={bem(null, [{ 'full-width': fullWidth }, { inline }], [className])}
        alt={alt}
        style={style}
        src={finalSrc || null}
        srcSet={finalSrcSet}
        {...otherProps}
      />
    );
  }
}

// only re-render image if the src changed
export default React.memo(Image, (prevProps, nextProps) => {
  return prevProps.src === nextProps.src;
});
